import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import FR from "country-flag-icons/react/3x2/FR";
import GB from "country-flag-icons/react/3x2/GB";
import IT from "country-flag-icons/react/3x2/IT";
import ES from "country-flag-icons/react/3x2/ES";
import DE from "country-flag-icons/react/3x2/DE";
import PT from "country-flag-icons/react/3x2/PT";
import CN from "country-flag-icons/react/3x2/CN";
import PL from "country-flag-icons/react/3x2/PL";
import RU from "country-flag-icons/react/3x2/RU";
import UA from "country-flag-icons/react/3x2/UA";
import RO from "country-flag-icons/react/3x2/RO";
// Wave 2 (2026-02-18): 6 new locales
import IS from "country-flag-icons/react/3x2/IS";
import SA from "country-flag-icons/react/3x2/SA";
import IN from "country-flag-icons/react/3x2/IN";
import KR from "country-flag-icons/react/3x2/KR";
import JP from "country-flag-icons/react/3x2/JP";
// Wave 3 (2026-02-19): Nordic
import SE from "country-flag-icons/react/3x2/SE";
import NO from "country-flag-icons/react/3x2/NO";

const LANGUAGES = [
  { code: "fr", label: "Français",  Flag: FR },
  { code: "en", label: "English",   Flag: GB },
  { code: "it", label: "Italiano",  Flag: IT },
  { code: "es", label: "Español",   Flag: ES },
  { code: "de", label: "Deutsch",   Flag: DE },
  { code: "pt", label: "Português", Flag: PT },
  { code: "zh", label: "中文",       Flag: CN },
  { code: "pl", label: "Polski",    Flag: PL },
  { code: "ru", label: "Русский",   Flag: RU },
  { code: "uk", label: "Українська", Flag: UA },
  { code: "ro", label: "Română",    Flag: RO },
  { code: "is", label: "Íslenska",  Flag: IS },
  { code: "ar", label: "العربية",   Flag: SA },
  { code: "hi", label: "हिन्दी",       Flag: IN },
  { code: "ta", label: "தமிழ்",       Flag: IN },
  { code: "ko", label: "한국어",     Flag: KR },
  { code: "ja", label: "日本語",     Flag: JP },
  { code: "sv", label: "Svenska",   Flag: SE },
  { code: "nb", label: "Norsk",     Flag: NO },
];

export default function LanguageSwitcher() {
  const { locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = LANGUAGES.find((l) => l.code === locale) || LANGUAGES[0];
  const CurrentFlag = current.Flag;

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        data-testid="lang-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Change language (current: ${current.label})`}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="transition-all hover:bg-white/15"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.4rem",
          padding: "0.35rem 0.55rem",
          background: "rgba(255,255,255,0.10)",
          border: "1px solid rgba(255,255,255,0.5)",
          backdropFilter: "blur(4px)",
          color: "white",
          cursor: "pointer",
          boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
        }}
      >
        <CurrentFlag
          data-testid={`lang-current-flag-${current.code}`}
          title={current.label}
          style={{ width: 22, height: 16, borderRadius: 2, boxShadow: "0 0 0 1px rgba(255,255,255,0.35)" }}
        />
        <svg width="9" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
          <path d="M1 1 L5 5 L9 1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          data-testid="lang-switcher-menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: "160px",
            background: "rgba(245,240,228,0.96)",
            backdropFilter: "blur(20px) saturate(180%)",
            WebkitBackdropFilter: "blur(20px) saturate(180%)",
            border: "1px solid rgba(20,16,12,0.12)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
            zIndex: 80,
            padding: "0.35rem 0",
            margin: 0,
            listStyle: "none",
          }}
        >
          {LANGUAGES.map(({ code, label, Flag }) => {
            const active = code === locale;
            return (
              <li key={code} role="option" aria-selected={active}>
                <button
                  type="button"
                  data-testid={`lang-option-${code}`}
                  onClick={() => { setLocale(code); setOpen(false); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.65rem",
                    width: "100%",
                    padding: "0.5rem 0.85rem",
                    background: active ? "rgba(20,16,12,0.06)" : "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--ink)",
                    fontFamily: "inherit",
                    fontSize: "0.85rem",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "rgba(20,16,12,0.04)"; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                >
                  <Flag style={{ width: 22, height: 16, borderRadius: 2, boxShadow: "0 0 0 1px rgba(20,16,12,0.12)" }} />
                  <span style={{ letterSpacing: "0.04em" }}>{label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
